import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, Subject, Subscription, fromEvent, merge } from 'rxjs';

import { AppLogger, AppLoggerService } from '../../core/logging';
import {
  AddItemRequest,
  LocalShoppingListItem,
  ShoppingListItem,
  localFromServer,
  mergeServerIntoLocal,
} from '../../models/shopping-list';
import { HouseholdEventsService } from '../events/household-events.service';
import { ShoppingService } from './shopping.service';

/** Periodic retry kick for the queue, in case nothing else fires. */
const RETRY_KICK_MS = 15_000;

/**
 * Owns the local representation of the active shopping list:
 *
 *   - In-memory queue of pending POST creations.
 *   - Optimistic updates: items appear immediately with `pending_sync` and
 *     are reconciled when the server responds.
 *   - Live SSE stream (via HouseholdEventsService) that refetches and merges
 *     the server's view whenever another client changes this list, leaving
 *     still-unsynced local items untouched.
 *   - Online/visibility handlers: drains the queue and refetches when the
 *     network comes back or the tab becomes visible again.
 *
 * The service is intentionally narrow: it does not deal with trips,
 * suggestions, or update/delete flows — those still go through the
 * regular ShoppingService for now.
 */
@Injectable({ providedIn: 'root' })
export class ShoppingSyncService {
  private readonly shoppingService = inject(ShoppingService);
  private readonly events = inject(HouseholdEventsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly log: AppLogger = inject(AppLoggerService).createForClass(ShoppingSyncService);

  /** Active list scope. `null` means "no active list, do nothing". */
  private householdId: number | null = null;
  private listId: number | null = null;

  /** Authoritative local item list (signal so components can react). */
  private readonly _items = signal<LocalShoppingListItem[]>([]);
  readonly items = this._items.asReadonly();

  /** Convenience views. */
  readonly pendingItems = computed(() => this._items().filter(i => i.state === 'pending'));
  readonly doneItems = computed(() => this._items().filter(i => i.state === 'done'));

  /**
   * Fires after every successful server refetch (triggered by a live event,
   * stream reconnect, or a visibility/online catch-up). Subscribers use this
   * to refresh sibling state (e.g. trips) without running their own timer.
   */
  private readonly _polled = new Subject<void>();
  readonly polled$: Observable<void> = this._polled.asObservable();

  /** FIFO of `client_id`s waiting to be POSTed. */
  private readonly queue: string[] = [];
  private draining = false;

  private streamSub: Subscription | null = null;
  private retryTimer: ReturnType<typeof setInterval> | null = null;

  constructor() {
    // Resume outbound sync and refetch when the network comes back.
    merge(fromEvent(window, 'online'))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.drainQueue();
        this.refetchItems();
      });

    // When the tab becomes visible again, refetch once to catch up on anything
    // missed while it was backgrounded. The SSE stream itself stays open.
    fromEvent(document, 'visibilitychange')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (!document.hidden) this.refetchItems();
      });

    // Stop everything on service destruction.
    this.destroyRef.onDestroy(() => {
      this.unsubscribeStream();
      this.stopRetryKick();
      this._polled.complete();
    });
  }

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  /**
   * Switch the service to a new (household, list) scope.
   * Replaces local state with the given server items and (re)subscribes the
   * live stream for the new scope.
   */
  setActiveList(householdId: number, listId: number, initialItems: ShoppingListItem[]): void {
    this.householdId = householdId;
    this.listId = listId;
    this._items.set(initialItems.map(localFromServer));
    this.subscribeToStream();
    this.startRetryKick();
  }

  /** Clear the active list (e.g. while the next list is loading). */
  clearActiveList(): void {
    this.householdId = null;
    this.listId = null;
    this._items.set([]);
    this.unsubscribeStream();
    this.stopRetryKick();
  }

  // ── Public mutations ──────────────────────────────────────────────────────

  /**
   * Optimistically add a raw-input item. The item appears immediately
   * with `sync_status: 'pending_sync'` and is queued for POST.
   *
   * Returns the locally-generated `client_id`.
   */
  enqueueRawInput(rawInput: string): string {
    const clientId = this.generateClientId();
    const local: LocalShoppingListItem = {
      client_id: clientId,
      state: 'pending',
      title: rawInput,
      raw_input: rawInput,
      sync_status: 'pending_sync',
    };
    this._items.update(items => [local, ...items]);
    this.queue.push(clientId);
    this.drainQueue();
    return clientId;
  }

  /**
   * Optimistically add an item from a known product. The item appears
   * immediately with `sync_status: 'pending_sync'`, the given placeholder
   * title (typically the product name), and is queued for POST with the
   * `{ client_id, product_id }` payload.
   */
  enqueueProduct(productId: number, displayTitle: string, displaySubtitle?: string | null): string {
    const clientId = this.generateClientId();
    const local: LocalShoppingListItem = {
      client_id: clientId,
      state: 'pending',
      title: displayTitle,
      subtitle: displaySubtitle ?? null,
      product_id: productId,
      // raw_input is unused in product mode but the field is required;
      // store the display title so a manual retry-as-raw is at least sane.
      raw_input: displayTitle,
      sync_status: 'pending_sync',
    };
    this._items.update(items => [local, ...items]);
    this.queue.push(clientId);
    this.drainQueue();
    return clientId;
  }

  /**
   * Optimistically add a pre-resolved (structured) item: a known title with an
   * optional subtitle and product-kind key, stored verbatim (no server parse).
   * Used for re-adding "Frequently Bought" suggestions that have no linked
   * product. Returns the locally-generated `client_id`.
   */
  enqueueStructured(title: string, subtitle?: string | null, productKey?: string | null): string {
    const clientId = this.generateClientId();
    const local: LocalShoppingListItem = {
      client_id: clientId,
      state: 'pending',
      source: 'structured_input',
      title,
      subtitle: subtitle ?? null,
      product_key: productKey ?? null,
      // raw_input is unused in structured mode but the field is required;
      // keep the title so a manual retry-as-raw is at least sensible.
      raw_input: title,
      sync_status: 'pending_sync',
    };
    this._items.update(items => [local, ...items]);
    this.queue.push(clientId);
    this.drainQueue();
    return clientId;
  }

  /** Mark a failed item for retry. Re-enqueues it and kicks the queue. */
  retry(clientId: string): void {
    const item = this._items().find(i => i.client_id === clientId);
    if (!item || item.sync_status === 'pending_sync') return;
    this.patchByClientId(clientId, { sync_status: 'pending_sync', last_sync_error: undefined });
    if (!this.queue.includes(clientId)) this.queue.push(clientId);
    this.drainQueue();
  }

  /**
   * Optimistic toggle of an item's done/pending state. Only meaningful
   * once the item has a server-side `id`. For items still pending sync
   * this is a local-only toggle; it will be re-resolved when the server
   * response arrives.
   *
   * Resolves after the server PATCH completes (or immediately for
   * not-yet-synced items). Rejects on PATCH failure (after rolling back).
   */
  toggleState(clientId: string): Promise<void> {
    const item = this._items().find(i => i.client_id === clientId);
    if (!item) return Promise.resolve();
    const next = item.state === 'done' ? 'pending' : 'done';
    this.patchByClientId(clientId, { state: next });
    this.moveToTop(clientId);

    if (item.id == null || !this.householdId || !this.listId) {
      return Promise.resolve();
    }
    const hid = this.householdId;
    const listId = this.listId;
    const id = item.id;
    return new Promise<void>((resolve, reject) => {
      this.shoppingService.updateItem(hid, listId, id, { state: next }).subscribe({
        next: () => resolve(),
        error: err => {
          this.log.error('Failed to toggle item state', err, { clientId, id });
          // Roll back
          this.patchByClientId(clientId, { state: item.state });
          reject(err);
        },
      });
    });
  }

  /** Optimistic subtitle save. No-op for not-yet-synced items. */
  updateSubtitle(clientId: string, subtitle: string | null): void {
    const item = this._items().find(i => i.client_id === clientId);
    if (!item) return;
    this.patchByClientId(clientId, { subtitle });
    this.moveToTop(clientId);
    if (item.id != null && this.householdId && this.listId) {
      this.shoppingService
        .updateItem(this.householdId, this.listId, item.id, { subtitle })
        .subscribe({
          error: err => {
            this.log.error('Failed to update subtitle', err, { clientId });
            this.patchByClientId(clientId, { subtitle: item.subtitle ?? null });
          },
        });
    }
  }

  /** Optimistic remove. Marks the item done server-side (no DELETE yet). */
  removeItem(clientId: string): void {
    const item = this._items().find(i => i.client_id === clientId);
    if (!item) return;
    this._items.update(items => items.filter(i => i.client_id !== clientId));

    if (item.id != null && this.householdId && this.listId) {
      this.shoppingService
        .updateItem(this.householdId, this.listId, item.id, { state: 'done' })
        .subscribe({
          error: err => {
            this.log.error('Failed to remove item', err, { clientId });
            // Re-insert on failure
            this._items.update(items => [...items, item]);
          },
        });
    }
  }

  // ── Queue draining ────────────────────────────────────────────────────────

  private drainQueue(): void {
    if (this.draining) return;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
    if (this.householdId == null || this.listId == null) return;
    if (this.queue.length === 0) return;

    this.draining = true;
    void this.processNext();
  }

  private async processNext(): Promise<void> {
    try {
      while (this.queue.length > 0) {
        if (typeof navigator !== 'undefined' && navigator.onLine === false) break;
        if (this.householdId == null || this.listId == null) break;

        const clientId = this.queue[0];
        const item = this._items().find(i => i.client_id === clientId);

        // Item was removed locally before sync — drop from queue.
        if (!item) {
          this.queue.shift();
          continue;
        }

        // Already synced (e.g. polling caught up first) — drop.
        if (item.sync_status === 'synced' && item.id != null) {
          this.queue.shift();
          continue;
        }

        const success = await this.postItem(item);
        if (success) {
          this.queue.shift();
        } else {
          // Stop the drain loop; will resume on `online` event or retry kick.
          break;
        }
      }
    } finally {
      this.draining = false;
    }
  }

  private postItem(item: LocalShoppingListItem): Promise<boolean> {
    const hid = this.householdId!;
    const listId = this.listId!;
    // Discriminator (only meaningful before the server assigns an `id`):
    //   - product card → carries `product_id`
    //   - structured (e.g. re-added "Frequently Bought") → source marks it,
    //     send the pre-resolved title/subtitle/kind verbatim
    //   - otherwise → raw-input, parsed server-side
    let payload: AddItemRequest;
    if (item.product_id != null && item.id == null) {
      payload = { client_id: item.client_id, product_id: item.product_id };
    } else if (item.source === 'structured_input' && item.id == null) {
      payload = {
        client_id: item.client_id,
        title: item.title,
        ...(item.subtitle ? { subtitle: item.subtitle } : {}),
        ...(item.product_key ? { product_kind_key: item.product_key } : {}),
      };
    } else {
      payload = { client_id: item.client_id, raw_input: item.raw_input };
    }

    return new Promise<boolean>(resolve => {
      this.shoppingService
        .addItem(hid, listId, payload)
        .subscribe({
          next: server => {
            this._items.update(items =>
              items.map(i =>
                i.client_id === item.client_id ? mergeServerIntoLocal(i, server) : i,
              ),
            );
            resolve(true);
          },
          error: err => {
            this.log.warn?.('Failed to sync shopping item; will retry', err);
            this.patchByClientId(item.client_id, {
              sync_status: 'failed',
              last_sync_error: this.errorMessage(err),
            });
            resolve(false);
          },
        });
    });
  }

  // ── Live stream (SSE) ───────────────────────────────────────────────────────

  /**
   * (Re)subscribe to the household's live event stream. A
   * `shopping_list.changed` event for the active list — or a fresh
   * (re)connect — triggers a full refetch that reconciles the server's view
   * into the local list. This replaces the old fixed-interval polling loop.
   */
  private subscribeToStream(): void {
    this.unsubscribeStream();
    if (this.householdId == null) return;

    this.streamSub = this.events.connect(this.householdId).subscribe(evt => {
      // `open` fires on every (re)connect — refetch to close any gap that
      // opened while we were disconnected.
      if (evt.event === 'open') {
        this.refetchItems();
        return;
      }
      if (evt.event === 'shopping_list.changed') {
        const listId = (evt.data as { list_id?: number } | null)?.list_id;
        // Refetch when the change targets the list we're showing. list_id is
        // absent only if the payload was dropped (size cap) — refetch then too.
        if (listId == null || listId === this.listId) this.refetchItems();
      }
    });
  }

  private unsubscribeStream(): void {
    this.streamSub?.unsubscribe();
    this.streamSub = null;
  }

  /**
   * Fetch the full item list, reconcile it into local state and notify
   * `polled$`. Triggered by live events and visibility/online catch-ups
   * rather than a timer.
   */
  private refetchItems(): void {
    if (this.householdId == null || this.listId == null) return;
    const hid = this.householdId;
    const listId = this.listId;
    this.shoppingService.getItems(hid, listId).subscribe({
      next: serverItems => {
        // Guard against late responses after a list switch.
        if (this.householdId !== hid || this.listId !== listId) return;
        this.reconcile(serverItems);
        this._polled.next();
      },
      error: err => this.log.warn?.('Item refetch failed', err),
    });
  }

  /**
   * Merge the server's view into the local list:
   *   - Server items override matching local items (by client_id, then id).
   *   - Local items still in `pending_sync` are preserved as-is.
   *   - Synced local items not present in the server response are removed
   *     (treated as deleted by another client).
   */
  private reconcile(serverItems: ShoppingListItem[]): void {
    this._items.update(local => {
      // Index local items so we can adopt the server's ordering (which is
      // sorted by `updated_at` desc) without losing local-only fields.
      const localByClientId = new Map<string, LocalShoppingListItem>();
      const localById = new Map<number, LocalShoppingListItem>();
      for (const item of local) {
        if (item.client_id) localByClientId.set(item.client_id, item);
        if (item.id != null) localById.set(item.id, item);
      }

      // Build the synced section in server order. Each entry merges the
      // corresponding local item (if any) so client-only fields survive.
      const synced: LocalShoppingListItem[] = serverItems.map(s => {
        const localMatch =
          (s.client_id && localByClientId.get(s.client_id)) ||
          localById.get(s.id);
        return localMatch
          ? mergeServerIntoLocal(localMatch, s)
          : localFromServer(s);
      });

      // Anything still pending sync or stuck in `failed` lives above the
      // synced section, in insertion order. (The queue is what owns these
      // until they reach the server.)
      const pending: LocalShoppingListItem[] = [];
      for (const item of local) {
        if (item.id != null) continue;            // synced items are handled above
        if (item.sync_status === 'pending_sync' || item.sync_status === 'failed') {
          pending.push(item);
        }
        // Anything else with no id and not pending/failed is dropped.
      }

      return [...pending, ...synced];
    });
  }

  // ── Periodic retry kick ───────────────────────────────────────────────────

  private startRetryKick(): void {
    this.stopRetryKick();
    this.retryTimer = setInterval(() => {
      if (this.queue.length > 0) this.drainQueue();
    }, RETRY_KICK_MS);
  }

  private stopRetryKick(): void {
    if (this.retryTimer != null) {
      clearInterval(this.retryTimer);
      this.retryTimer = null;
    }
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  private patchByClientId(clientId: string, patch: Partial<LocalShoppingListItem>): void {
    this._items.update(items =>
      items.map(i => (i.client_id === clientId ? { ...i, ...patch } : i)),
    );
  }

  /**
   * Move the matching item to the front of the list. Called after local
   * mutations so the item bumps up immediately, mirroring the server's
   * `updated_at`-desc ordering without waiting for the next poll.
   */
  private moveToTop(clientId: string): void {
    this._items.update(items => {
      const idx = items.findIndex(i => i.client_id === clientId);
      if (idx <= 0) return items;
      const item = items[idx];
      return [item, ...items.slice(0, idx), ...items.slice(idx + 1)];
    });
  }

  private generateClientId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    // Extremely unlikely fallback for ancient runtimes.
    return `cid-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  private errorMessage(err: unknown): string {
    if (err instanceof Error) return err.message;
    if (typeof err === 'object' && err !== null && 'message' in err) {
      return String((err as { message: unknown }).message);
    }
    return 'Sync failed';
  }
}


